import * as React from 'react';
import * as analytics from '../../analytics';
import { Attachment, IAttachmentActions } from "../../entities/Subentities";
import { AttachmentCreatePanel } from "./AttachmentCreatePanel";
import { FileUploader } from "./FileUploader";
import { ReplaceDialog, useActionsWithReplace } from "./ReplaceDialog";
import { ApplicationState } from '../../store';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as NotificationsStore from '../../store/NotificationsStore';
import { UserState } from '../../store/User';

type ActionProps = {
    notificationActions: typeof NotificationsStore.actionCreators;
};

type Props = {
    showToastMessage?: boolean;

    attachments: Attachment[];
    isOpen: boolean;
    onDismiss: () => void;
    uploaderRef: React.RefObject<HTMLInputElement>;
    blur?: boolean;
    readonly?: boolean;
    originActions?: IAttachmentActions;
} & ReturnType<typeof useActionsWithReplace> & { isLoading: boolean } & ActionProps;

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.projectsList.isAttachmentUploading
})

const mergeActionCreators = (dispatch: any): ActionProps => ({
    notificationActions: bindActionCreators(NotificationsStore.actionCreators, dispatch)
});

export const AttachmentsComponent = connect(mapStateToProps, mergeActionCreators)((props: React.PropsWithChildren<Props>) => {
    const { uploaderRef, isOpen, onDismiss, readonly, blur, attachments,
        actions, originActions, setReplaceData, replaceData, showToastMessage } = props;
    const onFilePicked = showToastMessage
        ? (files: File[]) => {
            actions?.uploadFile?.(files);
            props.notificationActions.pushNotification({ message: "The file upload has started" });
        }
        : actions?.uploadFile;
    return <FileUploader
        readonly={readonly}
        blur={blur}
        upoaderRef={uploaderRef}
        onFilePicked={onFilePicked}>
        {props.children}
        {
            isOpen && <AttachmentCreatePanel
                onDismiss={onDismiss}
                actions={actions}
            />
        }
        {
            !!replaceData && !!originActions && <ReplaceDialog
                data={replaceData}
                attachments={attachments}
                actions={originActions}
                onDismiss={() => setReplaceData(undefined)}
            />
        }
    </FileUploader>
})

export const useAttachmentsComponent = <TActions extends IAttachmentActions,>(attachments: Attachment[], user: UserState, originActions?: TActions) => {
    const { attachmentsMenuItem, componentProps, add, upload } = useAttachmentsMenu(user);
    const { replaceData, setReplaceData, actions } = useActionsWithReplace(attachments, originActions);
    return {
        menu: {
            attachmentsMenuItem,
            add,
            upload
        },
        componentProps: {
            ...componentProps,
            replaceData,
            setReplaceData,
            actions,
            originActions,
            attachments
        }
    }
}

const useAttachments = () => {
    const uploaderRef = React.useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    return {
        onAddClick: () => setIsOpen(true),
        onUploadClick: () => uploaderRef.current?.click(),
        componentProps: {
            uploaderRef,
            isOpen,
            onDismiss: () => setIsOpen(false)
        }
    }
}

const useAttachmentsMenu = (user: UserState) => {
    const { onAddClick, onUploadClick, componentProps } = useAttachments();

    const add = {
        text: "Add Link",
        onClick: () => {
            onAddClick?.();
            analytics.trackEvent("Add Link", user);
        }
    }

    const upload = {
        text: "Upload from Computer",
        onClick: () => {
            onUploadClick?.();
            analytics.trackEvent("Upload from Computer", user);
        }
    }

    const attachmentsMenuItem = {
        key: "add",
        iconProps: { iconName: "Add" },
        text: 'New Attachment',
        subMenuProps: {
            items: [{
                key: 'add-link',
                iconProps: { iconName: 'Link' },
                ...add
            },
            {
                key: 'upload',
                iconProps: { iconName: 'Attach' },
                ...upload
            }]
        }
    }

    return { attachmentsMenuItem, componentProps, add, upload };
}